import { ref } from 'vue'
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  updateDoc,
  Timestamp,
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase'
import { useAuth } from './useAuth'

// Open Graph standard size
const OG_WIDTH = 1200
const OG_HEIGHT = 630

/**
 * Cover-crops a (WebGL) canvas into a 1200x630 JPEG blob.
 * Must be called synchronously right after a render so the drawing
 * buffer is still valid (renderers don't use preserveDrawingBuffer).
 */
export function canvasToOgBlob(source: HTMLCanvasElement): Promise<Blob | null> {
  if (!source.width || !source.height) return Promise.resolve(null)

  const target = document.createElement('canvas')
  target.width = OG_WIDTH
  target.height = OG_HEIGHT
  const ctx = target.getContext('2d')
  if (!ctx) return Promise.resolve(null)

  const scale = Math.max(OG_WIDTH / source.width, OG_HEIGHT / source.height)
  const drawW = source.width * scale
  const drawH = source.height * scale
  ctx.drawImage(source, (OG_WIDTH - drawW) / 2, (OG_HEIGHT - drawH) / 2, drawW, drawH)

  return new Promise((resolve) => target.toBlob(resolve, 'image/jpeg', 0.85))
}

export function useThumbnails() {
  const { currentUser } = useAuth()
  const isUploadingThumbnail = ref(false)

  /**
   * Uploads a thumbnail to a deterministic path (overwrites previous one),
   * then refreshes thumbnailUrl on the file doc and on any existing shares.
   * Re-uploading invalidates the old download token, hence the doc updates.
   */
  async function uploadThumbnail(fileId: string, blob: Blob): Promise<string | null> {
    if (!currentUser.value) return null
    const uid = currentUser.value.uid
    const path = `users/${uid}/thumbnails/${fileId}.jpg`

    isUploadingThumbnail.value = true
    try {
      const thumbRef = storageRef(storage, path)
      await uploadBytes(thumbRef, blob, {
        contentType: 'image/jpeg',
        cacheControl: 'public, max-age=300',
      })
      const url = await getDownloadURL(thumbRef)

      await updateDoc(doc(db, 'users', uid, 'files', fileId), {
        thumbnailUrl: url,
        thumbnailPath: path,
        thumbnailUpdatedAt: Timestamp.now(),
      })

      const sharesQuery = query(
        collection(db, 'shares'),
        where('userId', '==', uid),
        where('fileId', '==', fileId)
      )
      const shares = await getDocs(sharesQuery)
      await Promise.all(shares.docs.map((d) => updateDoc(d.ref, { thumbnailUrl: url })))

      return url
    } finally {
      isUploadingThumbnail.value = false
    }
  }

  return {
    isUploadingThumbnail,
    uploadThumbnail,
  }
}
